import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Form from "react-bootstrap/Form";
import DamageType from "components/DamageType/DamageType";
import "./PestInformation.css";
import Select from "react-select";
import GridPaging from "components/Paging/GridPaging";
import SortDirectionIcon from "components/SortDirectionIcon";
import regionsImage from "assets/img/forest-service-regions.png";
import ToolButton from "components/ToolButton/ToolButton";

import {
  getDamageTypes,
  getPestSummaryByRegion,
  getAllYears,
  getAllregions,
} from "../../services/pestTableService";

const ForestPestByRegion = () => {
  const [selectedRegion, setSelectedRegion] = useState();
  const [selectedType, setSelectedType] = useState();
  const [regionList, setRegionList] = useState();
  const [typeList, setTypeList] = useState();
  const [yearList, setYearList] = useState(null);
  const [data, setData] = useState();
  const [currentPageData, setCurrentPageData] = useState();
  const [selectedCol1, setSelectedCol1] = useState();
  const [selectedCol2, setSelectedCol2] = useState();
  const [selectedCol3, setSelectedCol3] = useState();
  const [selectedCol4, setSelectedCol4] = useState();
  const [selectedCol5, setSelectedCol5] = useState();
  const [sortAscending, setSortAscending] = useState(true);
  const [sortedField, setSortedField] = useState(null);
  const [regionMapExpanded, setRegionMapExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [includeAbiotic, setIncludeAbiotic] = useState(false);
  const announcement = useRef(null);
  const totalPages = useRef(1);
  const initialLoad = useRef(true);
  const totalRecords = useRef(0);
  const years = useRef();
  const sortedData = useRef();
  const selectRegion = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  let foundRegion;
  let foundDamageType;
  let foundAbiotic = false;

  useEffect(() => {
    if (!initialLoad.current) {
      setLoading(true);
      setSortedField("Region");
      setSortAscending(true);
      setCurrentPage(1);
      getPestSummary();
    }
  }, [selectedRegion, selectedType, includeAbiotic]);

  useEffect(() => {
    if (data) {
      initialLoad.current = false;
      sortData(sortedField, sortAscending);
    }
  }, [data]);

  useEffect(() => {
    if (currentPageData) {
      setLoading(false);
    }
  }, [currentPageData]);

  useEffect(() => {
    setLoading(true);
    sliceDataIntoPages();
  }, [pageSize, currentPage]);

  // Initial load
  useEffect(() => {
    announcement.current = "You are now on the By Region table page";
    setLoading(true);
    const paramRegion = searchParams.get("rn");
    const paramDamageType = searchParams.get("dt");
    const paramAbiotic = searchParams.get("ab");
    // Regions
    getAllregions().then((result) => {
      const regionOptions = result.data;
      const allRegions = {
        value: 0,
        label: "All Regions",
        isDisabled: false,
      };
      regionOptions.unshift(allRegions);
      const listOfRegions = Array.from(regionOptions);
      setRegionList(listOfRegions);
      if (paramRegion) {
        foundRegion = listOfRegions.find(
          (element) => element.label === paramRegion
        );
        if (foundRegion) {
          setSelectedRegion(foundRegion);
        }
      }
    });
    // Damage Types
    getDamageTypes().then((result) => {
      const typesOptions = result.data;
      const listOfDamageTypes = Array.from(typesOptions);
      setTypeList(listOfDamageTypes);
      if (paramDamageType) {
        foundDamageType = listOfDamageTypes.find(
          (element) => element.label === paramDamageType
        );
        if (foundDamageType) {
          setSelectedType(foundDamageType);
        }
      }
    });
    // Abiotic
    if (paramAbiotic === "true") {
      setIncludeAbiotic(true);
      foundAbiotic = true;
    }
    getAllYears().then((result) => {
      years.current = result.data.map((item) => item.value).slice(0, 5);
      setYearList(years.current.slice(0, 5));
      setSelectedCol1(years.current.slice(0, 1));
      setSelectedCol2(years.current.slice(1, 2));
      setSelectedCol3(years.current.slice(2, 3));
      setSelectedCol4(years.current.slice(3, 4));
      setSelectedCol5(years.current.slice(4, 5));
      setSortedField("Region");
      getPestSummary();
    });
    document.title = "Forest Pest Conditions - By Region";
  }, []);

  const getPestSummary = () => {
    let filter;
    if (foundRegion) {
      filter = {
        damageTypeId: foundDamageType.value,
        Regionnumber: foundRegion.value,
        years: years.current,
      };
    } else {
      filter = {
        damageTypeId: selectedType ? selectedType["value"] : 93,
        Regionnumber: selectedRegion ? selectedRegion["value"] : 0,
        years: years.current,
      };
    }
    getPestSummaryByRegion(filter).then((result) => {
      let filteredData;
      if (foundAbiotic || includeAbiotic) {
        filteredData = result.data.data.filter(
          (element) => element.IsAbiotic === 1
        );
      } else {
        filteredData = result.data.data.filter(
          (element) => element.IsAbiotic === 0
        );
      }
      totalRecords.current = filteredData.length;
      setData(filteredData);
    });
  };

  const formatNumber = (input) => {
    return Number(input).toLocaleString();
  };

  const toggleAbiotic = () => {
    if (!initialLoad.current) {
      setIncludeAbiotic((prevState) => !prevState);
      announcement.current =
        "The table has been filtered to " +
        (includeAbiotic ? "exclude" : "include") +
        " abiotic agents.";
    }
  };

  const toggleSort = (field) => {
    if (!initialLoad.current) {
      let asc = !sortAscending;
      setCurrentPage(1);
      if (sortedField === field) {
        setSortAscending((prevState) => !prevState);
        sortData(field, !sortAscending);
      } else {
        asc = true;
        setSortedField(field);
        setSortAscending(true);
        sortData(field, true);
      }
      announcement.current =
        "The table has been sorted by column " +
        field +
        " in " +
        (asc ? "ascending" : "descending") +
        " order";
    }
  };

  const sortData = (field, asc) => {
    sortedData.current = field
      ? [...data].sort((a, b) => {
          const aValue = isNaN(parseFloat(a[field]))
            ? a[field]
            : parseFloat(a[field]);
          const bValue = isNaN(parseFloat(b[field]))
            ? b[field]
            : parseFloat(b[field]);
          if (asc) {
            return aValue < bValue ? -1 : 1;
          } else {
            return aValue > bValue ? -1 : 1;
          }
        })
      : data;
    sliceDataIntoPages();
  };

  const sliceDataIntoPages = () => {
    if (sortedData.current) {
      totalPages.current = Math.ceil(sortedData.current.length / pageSize);
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = Math.min(
        startIndex + pageSize - 1,
        sortedData.current.length - 1
      );
      if (sortedData.current.length === 0) {
        announcement.current = "No records match your filter selections";
      }
      setCurrentPageData(sortedData.current.slice(startIndex, endIndex + 1));
    }
  };

  // Function to handle page size
  const handleChangePageSize = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  // Function to handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  function handlePrint() {
    const lblRegion =
      selectedRegion != null ? selectedRegion.label : "All Regions";
    const lblDamageType =
      selectedType != null ? selectedType.label : "All Damage Types";
    const table = document.getElementById(
      "hidden-table-for-copy-and-print"
    ).outerHTML;
    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(
      "<html><head><title>By Forest Service Region</title>"
    );
    printWindow.document.write("<style>");
    printWindow.document.write(
      "table {border-collapse: collapse; width: 100%;}  td, th { border:0.5px solid black; text-align:left; } th{background-color: #f0f0f0;} tr:nth-child(odd) { background-color: #edf1e6;} "
    );

    printWindow.document.write("</style>");
    printWindow.document.write("</head><body>");
    printWindow.document.write(
      "<h2>By Forest Service Regions</h2>" +
        "<h3>" +
        lblRegion +
        " - " +
        lblDamageType +
        " (acres) </h3>"
    );
    printWindow.document.write(table);
    printWindow.document.write("</body></html>");
    printWindow.document.close();
    printWindow.print();
  }
  // Copy to clipboard
  function handleCopy() {
    const tableHTML = document.getElementById(
      "hidden-table-for-copy-and-print"
    ).outerHTML;
    navigator.permissions.query({ name: "clipboard-write" }).then((result) => {
      if (result.state === "granted") {
        navigator.clipboard.writeText(tableHTML);
      }
    });
  }
  // Share the link
  function handleShare() {
    let url = window.location.href;
    if (url.indexOf("?") > -1) {
      url = url.substring(0, url.indexOf("?"));
    }
    const damageType =
      selectedType != null ? selectedType.label : "All Damage Types";
    const Region =
      selectedRegion != null ? selectedRegion.label : "All Regions";
    navigator.permissions.query({ name: "clipboard-write" }).then((result) => {
      if (result.state === "granted") {
        navigator.clipboard.writeText(
          url +
            "?dt=" +
            encodeURIComponent(damageType) +
            "&rn=" +
            encodeURIComponent(Region) +
            (includeAbiotic ? "&ab=true" : "")
        );
      }
    });
  }
  return (
    <main className="grid-container pest-information" id="main-content">
      <h1 className="main-heading">By Forest Service Region</h1>
      <center>
        <div className="usa-accordion pest-info-region-map">
          <button
            type="link"
            className="usa-accordion__button usa-accordion-button"
            aria-expanded="false"
            aria-controls="region-map"
            onClick={() => {
              setRegionMapExpanded((previousValue) => !previousValue);
            }}
          >
            Select to display a map of USFS regions{" "}
            <i
              className={
                (regionMapExpanded ? "fa fa-caret-up" : "fa fa-caret-down") +
                " carousel__i"
              }
            ></i>
          </button>
          <div id="region-map" className="usa-accordion__content">
            {regionMapExpanded && (
              <img
                src={regionsImage}
                id="forest-service-regions"
                alt="U.S. Forest Service regions"
                className="center"
              />
            )}
          </div>
        </div>
      </center>
      <br />
      <p className="pest-information-intro">
        The default view includes all damage types and biotic agents only,
        combined as damage displayed in acres. Abiotic agents (such as drought
        and frost) can be added by checking the "Include Abiotic Agents"
        checkbox below. Data are derived from the authoritative Forest Health
        Protection{" "}
        <a
          href="https://www.fs.usda.gov/science-technology/data-tools-products/fhp-mapping-reporting/detection-surveys"
          target="_blank"
          rel="noreferrer"
          className="usa-link"
        >
          Insect and Disease Survey
        </a>{" "}
        database. To filter by Forest Service region, select from the options
        below.{" "}
      </p>
      <DamageType />
      <br />
      <br />
      <div className="grid-row">
        <div className="tablet:grid-col-5 mobile-lg:grid-col-12">
          <span className="label_text">Select Forest Service Region</span>
          {
            <Select
              ref={selectRegion}
              className="react-select"
              placeholder="All Regions"
              aria-label="Select a Forest Service region. Press the down arrow to expand the option list. Press delete
              to clear the current selection."
              name="singleSelect"
              value={selectedRegion}
              onChange={(value) => setSelectedRegion(value)}
              options={regionList}
              hideselectedoptions="true"
              isClearable={true}
              styles={{
                placeholder: (baseStyles) => ({
                  ...baseStyles,
                  color: "#1b1b1b",
                }),
              }}
            />
          }
        </div>
        <div className="tablet:grid-col-4 mobile-lg:grid-col-12">
          <span className="label_text">Select Damage Type</span>
          {
            <Select
              className="react-select"
              placeholder="All Damage Types"
              aria-label="Select a damage type. Press the down arrow to expand the option list. Press delete
              to clear the current selection."
              name="singleSelect"
              value={selectedType}
              onChange={(value) => setSelectedType(value)}
              options={typeList}
              hideselectedoptions="true"
              isClearable={true}
              styles={{
                placeholder: (baseStyles) => ({
                  ...baseStyles,
                  color: "#1b1b1b",
                }),
              }}
            />
          }
        </div>
      </div>
      <br />
      <div className="grid-row">
        <div className="tablet:grid-col-3 mobile-lg:grid-col-12">
          <span className="show-records_label">Show </span>
          <select
            className="paging_select"
            value={pageSize}
            onChange={handleChangePageSize}
            name="singleSelect"
            placeholder="All"
            aria-label="All"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value={data?.length}>All</option>
          </select>
          <span className="show-records_label"> Records</span>
        </div>
        <div
          id="abiotic-checkbox-container"
          className="tablet:grid-col-6 mobile-lg:grid-col-12"
        >
          <Form.Check
            type="checkbox"
            checked={includeAbiotic}
            label="Include Abiotic Agents"
            id="abiotic-checkbox"
            onClick={() => toggleAbiotic()}
            onKeyDown={(e) => {
              if (e.key === "Enter") toggleAbiotic();
            }}
            readOnly
          />
        </div>
        <div className="tablet:grid-col-3 mobile-lg:grid-col-12">
          <ToolButton
            currentPage={currentPage}
            handlePrint={handlePrint}
            handleCopy={handleCopy}
            handleShare={handleShare}
          ></ToolButton>
        </div>
      </div>
      <div className="visually-hidden" aria-live="polite" aria-busy={loading}>
        {announcement.current}
      </div>
      <div className="grid-col-12 usa-table-container--scrollable">
        {loading ? (
          <div className="spinner-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <div>
            {currentPageData?.length > 0 ? (
              <table
                aria-label="Pest Information by Forest Service Region"
                id="byregiontable"
                className="usa-table"
              >
                <thead>
                  <tr>
                    <th
                      tabIndex="0"
                      onClick={() => toggleSort("Region")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") toggleSort("Region");
                      }}
                      scope="col"
                      aria-label="Region (sortable)"
                    >
                      REGION
                      <SortDirectionIcon
                        asc={sortAscending}
                        visible={sortedField === "Region"}
                      />
                    </th>
                    {yearList?.map((year) => (
                      <th
                        tabIndex="0"
                        key={year}
                        onClick={() => toggleSort(year)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") toggleSort(year);
                        }}
                        scope="col"
                        aria-label={year + " acres (sortable)"}
                      >
                        {year}
                        {" (acres)"}
                        <SortDirectionIcon
                          asc={sortAscending}
                          visible={sortedField === year}
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentPageData.map((item, index) => (
                    <tr key={item["Region"]} id={item["Region"]}>
                      <td>{item["Region"]}</td>
                      <td>{formatNumber(item[selectedCol1])}</td>
                      <td>{formatNumber(item[selectedCol2])}</td>
                      <td>{formatNumber(item[selectedCol3])}</td>
                      <td>{formatNumber(item[selectedCol4])}</td>
                      <td>{formatNumber(item[selectedCol5])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="label_text">
                No records match your filter selections
              </p>
            )}
          </div>
        )}
      </div>
      <GridPaging
        currentPage={currentPage}
        totalPages={totalPages.current}
        handlePageChange={handlePageChange}
        pageSize={pageSize}
        totalRecords={totalRecords.current}
      />
      {sortedData.current && (
        <div
          id="hidden-table-for-copy-and-print"
          className="grid-col-12 usa-table-container--scrollable"
          aria-hidden="true"
        >
          <table>
            <thead>
              <tr>
                <th>REGION</th>
                {yearList?.map((year) => (
                  <th key={year}>
                    {year}
                    {" (acres)"}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedData.current.map((item, index) => (
                <tr key={item["Region"]} id={item["Region"]}>
                  <td>{item["Region"]}</td>
                  <td>{formatNumber(item[selectedCol1])}</td>
                  <td>{formatNumber(item[selectedCol2])}</td>
                  <td>{formatNumber(item[selectedCol3])}</td>
                  <td>{formatNumber(item[selectedCol4])}</td>
                  <td>{formatNumber(item[selectedCol5])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
};

export default ForestPestByRegion;
